import dayjs from 'dayjs';
import debug from 'debug';
import { formatTokenCount, safeFormat } from '../utils/static.tools';
import { LogLevel, resolveLevel } from './logger.entry';
import type { LoggerTransport } from './logger.entry';
import Bowser from 'bowser';

export interface DebugLogStreamOptions {
  logName: string;
  useSubLogName: boolean;
}
export class DebugLogStream implements LoggerTransport {
  private _log: debug.Debugger | undefined;
  private _levelColor: Map<string, string> = new Map();
  private _minLevel = Number.MAX_SAFE_INTEGER;
  constructor(private _options: DebugLogStreamOptions) {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const supportLocation = !!(
      browser.satisfies({ chrome: '>=4' }) ||
      browser.satisfies({ Chromium: '>=4' }) ||
      browser.satisfies({ edge: '>=79' }) ||
      browser.satisfies({ firefox: '>=73' })
    );
    if (supportLocation) {
      this._log = debug(_options.logName);
    }

    this._levelColor.set('trace', `color:#ffffff; background:blue; padding:0.2em;`);
    this._levelColor.set('debug', `color:#ffffff; background:green; padding:0.2em;`);
    this._levelColor.set('info', `color:#000000; background:cyan; padding:0.2em;`);
    this._levelColor.set('warn', `color:#000000; background:yellow; padding:0.2em;`);
    this._levelColor.set('error', `color:#ffffff; background:red; padding:0.2em;`);
    this._levelColor.set('fatal', `color:#ffffff; background:magenta; padding:0.2em;`);
    const localDebugLevel = localStorage.getItem('debug-level');
    if (localDebugLevel) {
      const minLevel = resolveLevel(localDebugLevel);
      if (minLevel >= 0) {
        this._minLevel = minLevel;
      }
    }
  }
  write(
    level: LogLevel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields: { [key: string]: any },
    msg: string,
    ...args: any[]
  ): void {
    const lvl = resolveLevel(level);
    if (lvl < this._minLevel) {
      return;
    }
    fields = { ...fields };
    const levelName = level;
    const time = dayjs(new Date());
    const tokenCount = formatTokenCount(msg);
    msg = safeFormat(msg, ...args.slice(0, tokenCount));
    const leftArgs = args.slice(tokenCount);
    const levelColor = this._levelColor.get(levelName) ?? 'color:#ffffff; background:blue; padding:0.2em;';
    if (!this._log) {
      if (Object.getOwnPropertyNames(fields).length > 0) {
        // eslint-disable-next-line no-console
        console.log(`%c${levelName}`, levelColor, `${msg}`, ...leftArgs, fields);
      } else {
        // eslint-disable-next-line no-console
        console.log(`%c${levelName}`, levelColor, `${msg}`, ...leftArgs);
      }
      return;
    }

    const useLog = this._log;

    if (Object.getOwnPropertyNames(fields).length > 0) {
      useLog(
        `%c${levelName} [${time.format('HH:mm:ss.SSS')}]`,
        levelColor,
        `${msg}`,
        ...leftArgs,
        '\n[Extra fields]',
        fields
      );
    } else {
      useLog(`%c${levelName} [${time.format('HH:mm:ss.SSS')}]`, levelColor, `${msg}`, ...leftArgs);
    }
  }
}
